import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export type Article = (typeof articles)[number];
export type Category = (typeof categories)[number];

export function getArticleById(id: string | undefined): Article | undefined {
  if (!id) return undefined;
  return articles.find((item) => item.id === id);
}

export function getCategoryBySlug(slug: string | undefined): Category | undefined {
  if (!slug) return undefined;
  return categories.find((item) => item.slug === slug);
}

export function getCategoryName(slug: string): string {
  return getCategoryBySlug(slug)?.name ?? "黑料";
}

export function getFeaturedArticles(): Article[] {
  const featured = articles.filter((item) => item.featured);
  return featured.length > 0 ? featured : articles.slice(0, 4);
}

export function getLatestArticles(limit?: number): Article[] {
  const sorted = [...articles].sort((a, b) => (a.date < b.date ? 1 : -1));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getHotArticles(limit?: number): Article[] {
  const sorted = [...articles].sort((a, b) => b.heat - a.heat);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getArticlesByCategory(slug: string): Article[] {
  return articles.filter((item) => item.category === slug);
}

export type SortKey = "latest" | "hot";

export function sortArticles(list: Article[], sort: SortKey): Article[] {
  const cloned = [...list];
  if (sort === "hot") {
    return cloned.sort((a, b) => b.heat - a.heat);
  }
  return cloned.sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function searchArticles(query: string): Article[] {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return [];
  return articles
    .map((item) => {
      const title = item.title.toLowerCase();
      const summary = item.summary.toLowerCase();
      const tags = item.tags.join(" ").toLowerCase();
      const category = getCategoryName(item.category).toLowerCase();
      let score = 0;
      if (title.includes(keyword)) score += 5;
      if (summary.includes(keyword)) score += 3;
      if (tags.includes(keyword)) score += 2;
      if (category.includes(keyword)) score += 2;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || (a.item.date < b.item.date ? 1 : -1))
    .map((entry) => entry.item);
}

export function getRelatedArticles(article: Article, limit = 4): Article[] {
  const sameCategory = articles.filter(
    (item) => item.id !== article.id && item.category === article.category,
  );
  const tagMatch = articles.filter(
    (item) =>
      item.id !== article.id &&
      item.category !== article.category &&
      item.tags.some((tag) => article.tags.includes(tag)),
  );
  const others = articles.filter(
    (item) => item.id !== article.id && !sameCategory.includes(item) && !tagMatch.includes(item),
  );
  return [...sameCategory, ...tagMatch, ...others].slice(0, limit);
}